import React, { useEffect, useState, useRef } from "react";
import { SectionCard } from "../study/course/SectionCard";
import AsyncTextToSpeech from "../study/AsyncTextToSpeech";
import AsyncSpeechEvaluation from "../study/AsyncSpeechEvaluation";
import { htmlToText, updateSpeakAloudActivity, nowISO } from "../../utils";
import { makeStyles } from "tss-react/mui";
import { useStudy } from "../../pages/study/hooks";
import { addAssignmentActivitiesByCourseAndAssignee } from "../../store/study/studyBaseApi";
import { Box, Grid, Typography } from "@mui/material";
import { ButtonBlue } from "../buttons";
import { useQuiz } from "../../pages/quiz/hooks";
import AsyncTextToSpeech2 from "../study/AsyncTextToSpeech2";
import arrowSvg from "../../assets/svgs/arrow.svg";
import { useCourse } from "../../pages/course/hooks";

// Mirrors MAX_RETRIES in AsyncSpeechEvaluation/SpeechEvaluation: the number of
// incorrect tries before a sentence is "given up". Stamped on the give-up
// INCORRECT_SPEAK_ALOUD record so the needed-support metric can count it as one
// struggled sentence.
const MAX_RETRIES = 3;

const CONTENT_SEPARATOR = ";";

// The sentence list this session is read from, most-trusted source first:
// parsed markup -> the flat contentArray ChapterRenderers already derived ->
// a last-resort split of the raw content. Returning [] here is what drives the
// "content unavailable" message instead of a recorder with nothing to score.
const deriveSentences = (data = {}) => {
  const { parsedMarkupContent, contentArray, content } = data;

  if (Array.isArray(parsedMarkupContent) && parsedMarkupContent.length) {
    return parsedMarkupContent;
  }

  if (Array.isArray(contentArray) && contentArray.length) {
    return contentArray
      .map((e) => (typeof e === "string" ? e : e?.text))
      .filter((text) => typeof text === "string" && text.trim())
      .map((text) => ({ text, image: null }));
  }

  if (typeof content === "string" && content.trim()) {
    return content
      .split(CONTENT_SEPARATOR)
      .map((e) => e.trim())
      .filter((e) => e)
      .map((text) => ({ text, image: null }));
  }

  return [];
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    height: "90%",
    background: "#fff",
  },
  actionBtn: {
    // marginTop: theme.spacing(8),
    // justifyContent: "center",
    // display: "flex"
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
  },
  containerBox: {
    // margin: theme.spacing(1),
    background: "#fff",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    height: "100%",
    overflowY: "scroll",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#E1E4E6",
      borderRadius: "10px",
    },
    "&:hover, &:focus-within": {
      "&::-webkit-scrollbar-thumb": {
        background:
          "linear-gradient(193.28deg, #29ABE2 5.42%, #2DA2E2 12.18%, #398CE2 33.81%, #417EE2 54.77%, #447AE2 73.02%)",
        width: "12px",
        cursor: "pointer",
      },
    },
  },
  gridContainer: {
    height: "100%",
  },
}));

export default function StoryWithKeywords({
  data,
  sectionId,
  sectionType,
  courseId,
  onComplete,
  onNextChapter,
}) {
  const { classes } = useStyles();
  const { profile } = useStudy();
  const { lastSectionChapter } = useCourse();
  const [chapter, setChapter] = useState({});
  const [content, setContent] = useState(data?.content || null);
  const [speechEvaluated, setSpeechEvaluated] = useState(false);
  const [completed, setCompleted] = React.useState(false);
  const startTime = React.useRef(nowISO());
  const [markupContent, setMarkupContent] = React.useState([]);
  const [parsedkeywords, setParsedKeywords] = React.useState([]);

  const speechEval = useRef({ start: null, end: null });

  const handleComplete = async () => {
    setCompleted(true);

    try {
      await addAssignmentActivitiesByCourseAndAssignee({
        params: { courseId, assigneeId: profile.id },
        body: {
          assigneeId: profile.id,
          courseId,
          sectionId,
          chapterId: chapter.chapterId,
          endTime: nowISO(),
          startTime: startTime.current,
          type: "CHAPTER_COMPLETE",
        },
      });
    } catch (e) {
      console.error(e);
    }

    // Signal completion only AFTER the CHAPTER_COMPLETE write above — the
    // session-gate verdict (retake vs celebrate) refetches the gate and must
    // see the finished attempt.
    if (onComplete && typeof onComplete === "function") {
      onComplete();
    }

    try {
      if (!lastSectionChapter.chapter) {
        await onNextChapter();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const onSpeechEvaluationCompletion = async (res, speechList) => {
    if (speechEvaluated) return;

    setSpeechEvaluated(true);

    speechEval.current.end = nowISO();

    //Call activities API
    try {
      await addAssignmentActivitiesByCourseAndAssignee({
        params: { courseId, assigneeId: profile.id },
        body: {
          assigneeId: profile.id,
          courseId,
          sectionId,
          chapterId: chapter.chapterId,
          endTime: speechEval.current.end,
          startTime: speechEval.current.start,
          type: "SPEAK_ALOUD",
          // audio: JSON.stringify(recordedSpeechList)
        },
      });
      await updateSpeakAloudActivity({
        speechList: speechList,
        profileId: profile.id,
        chapterId: chapter.chapterId,
        sectionId,
        end: speechEval.current.end,
        start: speechEval.current.start,
        courseId,
        type: "SPEAK_ALOUD",
      });
    } catch (e) {
      console.error(e);
    }
  };

  const onSpeechEvaluationStart = () => {
    speechEval.current.start = nowISO();
  };

  const onSpeechEvaluationFailureThresholdReached = async ({ audio, meta }) => {
    if (!audio || !meta || !Array.isArray(meta) || !meta.length) return;

    // Collapse the give-up into a SINGLE sentence-level record (was one row per
    // failed word). retryCount = MAX_RETRIES marks "gave up after 3 tries" so the
    // needed-support metric counts it as one struggled sentence. Score stays 0, so
    // it never enters any reading average (every average path ignores score 0 /
    // INCORRECT_SPEAK_ALOUD).
    const sentenceText = meta
      .map((e) => (e && e.word ? e.word : e))
      .filter(Boolean)
      .join(" ");
    let _speechList = [
      {
        text: sentenceText,
        outcome: {},
        score: 0,
        audioUrl: audio,
        retryCount: MAX_RETRIES,
      },
    ];

    try {
      await updateSpeakAloudActivity({
        speechList: _speechList,
        profileId: profile.id,
        chapterId: chapter.chapterId,
        end: nowISO(),
        start: speechEval.current.start,
        sectionId,
        courseId,
        type: "INCORRECT_SPEAK_ALOUD",
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!data || !Object.keys(data).length) return;

    // New session: re-stamp the start timestamp and reset speech state so this session
    // records its own start time (not the previous session's — the ref is created once
    // at mount and the component is reused across consecutive sessions) and can post its
    // own SPEAK_ALOUD activity.
    startTime.current = nowISO();
    speechEval.current = { start: null, end: null };
    setSpeechEvaluated(false);

    let { parsedMarkupContent, parsedmarkupKeywords, ...rest } = data;

    // Set unconditionally (including []): the old length guard left the PREVIOUS
    // session's sentences on screen when a new one resolved to nothing.
    setMarkupContent(deriveSentences(data));

    if (parsedmarkupKeywords) {
      parsedmarkupKeywords = parsedmarkupKeywords.map((e) => ({
        ...e,
        synonyms: String(e.synonyms)
          .split(",")
          .map((syn) => syn.trim()),
      }));
      setParsedKeywords(parsedmarkupKeywords);
    }

    setChapter(rest);
    setContent(rest?.content || null);

    // setTimeout(() => setSpeechEvaluated(true), 1500)
  }, [data]);

  if (!Object.keys(chapter).length) return <></>;

  return (
    <Box className={classes.containerBox}>
      <Grid container spacing={2} className={classes.gridContainer}>
        <Grid item xs={12} md={7} order={{ xs: 2, sm: 2, md: 1 }}>
          <Box>
            <SectionCard>
              <Box>
                <AsyncTextToSpeech2
                  contentArray={markupContent}
                  // readingComplete={reading}
                  id={chapter.chapterId}
                  contentType={"text"}
                  importantWords={parsedkeywords}
                  syncWithSpeechEvaluation={true}
                  speechEvaluated={speechEvaluated}
                />
              </Box>
            </SectionCard>
          </Box>
        </Grid>
        <Grid item xs={12} md={5} order={{ xs: 1, sm: 1, md: 2 }}>
          {!speechEvaluated && !completed ? (
            <Box
              sx={{
                background: "#fff",
                borderRadius: 1,
                boxShadow: "0px 4px 4px 0px #0000000A",
                height: "100%",
                alignContent: "center",
              }}
            >
              {!markupContent.length ? (
                <SectionCard>
                  <Typography
                    sx={{
                      fontFamily: '"Quicksand", sans-serif',
                      fontWeight: "600",
                      textAlign: "center",
                      color: "#464255",
                      p: 2,
                    }}
                  >
                    Reading content is unavailable for this session. Please
                    contact your teacher.
                  </Typography>
                </SectionCard>
              ) : (
                <SectionCard completed={speechEvaluated}>
                  <AsyncSpeechEvaluation
                    onComplete={onSpeechEvaluationCompletion}
                    onStart={onSpeechEvaluationStart}
                    onFailureThresholdReached={
                      onSpeechEvaluationFailureThresholdReached
                    }
                    sectionId={sectionId}
                    chapterId={data.chapterId}
                    importantWords={chapter.keyWords}
                    courseId={courseId}
                    saveHistory={sectionType === "story-reading-evaluation"}
                    skipFailures={sectionType === "story-reading-evaluation"}
                    question={markupContent}
                    dictionary={parsedkeywords}
                    sampleRate={16000}
                  />
                </SectionCard>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                background: "#fff",
                borderRadius: 1,
                boxShadow: "0px 4px 4px 0px #0000000A",
                height: "70vh",
                position: "relative",
              }}
            >
              {speechEvaluated && (
                <>
                  <Typography
                    sx={{
                      color: "#447AE2",
                      fontSize: "1.5em",
                      fontFamily: '"Quicksand", sans-serif',
                      fontWeight: "700",
                      textAlign: "center",
                    }}
                  >
                    Your Scores
                  </Typography>
                </>
              )}
              <SectionCard completed={completed}>
                <Box
                  sx={{
                    // py: 1,
                    height: "50vh",
                    display: "flex",
                    justifyContent: {
                      xs: "center",
                      sm: "center",
                      md: "flex-start",
                    },
                    overflowY: "scroll",
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#F5F5F5",
                      borderRadius: "10px",
                    },
                    "&:hover, &:focus-within": {
                      "&::-webkit-scrollbar-thumb": {
                        background:
                          "linear-gradient(193.28deg, #29ABE2 5.42%, #2DA2E2 12.18%, #398CE2 33.81%, #417EE2 54.77%, #447AE2 73.02%)",
                        width: "12px",
                        cursor: "pointer",
                      },
                    },
                  }}
                >
                  <AsyncTextToSpeech
                    contentArray={markupContent.map((item) => item.text)}
                    id={chapter.chapterId}
                    contentType={"text"}
                    importantWords={parsedkeywords}
                    syncWithSpeechEvaluation={true}
                    speechEvaluated={speechEvaluated}
                  />
                </Box>
              </SectionCard>
            </Box>
          )}
        </Grid>
      </Grid>
      <div className={classes.actionBtn}>
        {!completed && speechEvaluated && (
          <ButtonBlue onClick={handleComplete}>
            <Typography
              sx={{
                fontWeight: "600",
                fontFamily: '"Quicksand", sans-serif',
                fontSize: { xs: ".87em", sm: "1em", md: "1.2em", lg: "1.4em" },
                zIndex: 2,
              }}
            >
              Continue
            </Typography>
            <img
              style={{
                width: "15%",
                maxWidth: "100%",
                marginTop: "5px",
                height: "auto",
                zIndex: 2,
              }}
              src={arrowSvg}
              alt=""
            />
          </ButtonBlue>
        )}
      </div>
    </Box>
  );
}
